import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../api/client';

const DAY_TABS = [
  { value: '', label: '전체' },
  { value: 'monday', label: '월' },
  { value: 'tuesday', label: '화' },
  { value: 'wednesday', label: '수' },
  { value: 'thursday', label: '목' },
  { value: 'friday', label: '금' },
];

const DAY_KO = { monday: '월', tuesday: '화', wednesday: '수', thursday: '목', friday: '금', saturday: '토', sunday: '일' };
const DAY_ORDER = { monday: 0, tuesday: 1, wednesday: 2, thursday: 3, friday: 4 };

const TOTAL_SEATS = 44;

export default function AdminSchedules() {
  const [day, setDay] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [viewMode, setViewMode] = useState('seats'); // 'seats' | 'list'

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-schedules', day],
    queryFn: async () => {
      const result = await adminAPI.getSchedules({ day });
      if (!result.success) throw new Error(result.error || '스케줄을 불러올 수 없습니다');
      return result.data || result.schedules || [];
    },
  });

  const schedules = data || [];
  const nameQuery = nameSearch.trim().toLocaleLowerCase();
  const filtered = schedules.filter((s) =>
    (!day || s.day_of_week === day) &&
    (!nameQuery || (s.name || '').toLocaleLowerCase().includes(nameQuery))
  );

  // 노선+시간별 그룹핑 (선점 현황용)
  const busGroups = useMemo(() => {
    const groups = {};
    filtered.forEach((s) => {
      const key = `${s.day_of_week}|${s.direction}|${s.route_name}|${s.bus_time}`;
      if (!groups[key]) {
        groups[key] = {
          dayOfWeek: s.day_of_week,
          direction: s.direction,
          routeName: s.route_name,
          busTime: s.bus_time,
          schedules: [],
        };
      }
      groups[key].schedules.push(s);
    });

    return Object.values(groups).sort((a, b) => {
      const dayDiff = (DAY_ORDER[a.dayOfWeek] ?? 9) - (DAY_ORDER[b.dayOfWeek] ?? 9);
      if (dayDiff !== 0) return dayDiff;
      const dirDiff = a.direction === 'UP' ? -1 : 1;
      if (a.direction !== b.direction) return dirDiff;
      return a.busTime.localeCompare(b.busTime);
    });
  }, [filtered]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-charcoal">스케줄 현황</h2>
        <p className="text-sm text-sub-text mt-1">전체 자동 예약 스케줄</p>
      </div>

      {/* 뷰 모드 + 요일 + 사용자 이름 필터 */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
        {/* 뷰 모드 토글 */}
        <div className="flex gap-1 bg-white rounded-xl border border-border overflow-hidden w-fit">
          <button
            onClick={() => setViewMode('seats')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              viewMode === 'seats' ? 'bg-tayo text-white' : 'text-sub-text hover:bg-snow'
            }`}
          >
            선점 현황
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              viewMode === 'list' ? 'bg-tayo text-white' : 'text-sub-text hover:bg-snow'
            }`}
          >
            스케줄 목록
          </button>
        </div>

        {/* Day filter tabs */}
        <div className="flex gap-1 bg-white rounded-xl border border-border overflow-hidden w-fit">
          {DAY_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setDay(tab.value)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                day === tab.value
                  ? 'bg-tayo text-white'
                  : 'text-sub-text hover:bg-snow'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:ml-auto">
          <input
            type="search"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            placeholder="사용자 이름 검색"
            aria-label="사용자 이름 검색"
            className="w-full sm:w-48 px-4 py-2 rounded-xl border border-border text-sm text-charcoal placeholder:text-sub-text focus:outline-none focus:ring-2 focus:ring-tayo/30 bg-white"
          />
          {nameSearch && (
            <button
              type="button"
              onClick={() => setNameSearch('')}
              className="shrink-0 px-3 py-2 rounded-xl bg-gray-100 text-charcoal text-sm font-medium hover:bg-gray-200"
            >
              초기화
            </button>
          )}
        </div>
      </div>

      {nameQuery && viewMode === 'seats' && (
        <p className="text-xs text-sub-text">이름 검색 중에는 일치하는 사용자의 좌석만 표시됩니다.</p>
      )}

      {/* Error */}
      {error && (
        <div className="bg-coral/10 border border-coral/20 rounded-xl p-4">
          <p className="text-sm text-coral">{error.message}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-tayo" />
          <p className="mt-2 text-sub-text">로딩 중...</p>
        </div>
      )}

      {/* ============ 선점 현황 뷰 ============ */}
      {!isLoading && viewMode === 'seats' && (
        <div className="space-y-4">
          {busGroups.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-card py-16 text-center">
              <p className="text-sub-text">{nameQuery ? '검색 결과가 없습니다' : '데이터가 없습니다'}</p>
            </div>
          ) : (
            busGroups.map((group) => {
              const allocatedSeats = new Map();
              group.schedules.forEach((s) => {
                if (s.allocated_seat) {
                  allocatedSeats.set(s.allocated_seat, {
                    name: s.name || s.student_id || '?',
                    studentId: s.student_id,
                    preferences: s.seat_preferences,
                  });
                }
              });

              const allocCount = allocatedSeats.size;
              const congestionPct = Math.round((allocCount / TOTAL_SEATS) * 100);

              return (
                <div key={`${group.dayOfWeek}-${group.direction}-${group.routeName}-${group.busTime}`}
                  className="bg-white rounded-2xl shadow-card overflow-hidden"
                >
                  {/* 헤더 */}
                  <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-charcoal">
                        {DAY_KO[group.dayOfWeek]}
                      </span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        group.direction === 'UP' ? 'bg-tayo-light text-tayo' : 'bg-orange-50 text-orange-600'
                      }`}>
                        {group.direction === 'UP' ? '등교' : '하교'}
                      </span>
                      <span className="text-sm text-charcoal">{group.routeName}</span>
                      <span className="text-sm text-sub-text">{group.busTime}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                        nameQuery ? 'bg-tayo-light text-tayo' :
                        congestionPct >= 70 ? 'bg-rose-100 text-rose-700' :
                        congestionPct >= 40 ? 'bg-amber-100 text-amber-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {nameQuery ? `검색 ${allocCount}석` : `${allocCount}/${TOTAL_SEATS}석`}
                      </span>
                      <span className="text-xs text-sub-text">{group.schedules.length}명</span>
                    </div>
                  </div>

                  {/* 좌석 그리드 */}
                  <div className="p-4">
                    <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(11, 1fr)' }}>
                      {Array.from({ length: TOTAL_SEATS }, (_, i) => {
                        const seatNo = i + 1;
                        const user = allocatedSeats.get(seatNo);
                        return (
                          <div
                            key={seatNo}
                            title={user ? `${user.name} (${user.studentId || ''})` : `${seatNo}번 - ${nameQuery ? '검색 결과 외 좌석' : '비어있음'}`}
                            className={`relative aspect-square rounded text-[10px] font-bold flex items-center justify-center cursor-default transition-colors ${
                              user
                                ? 'bg-tayo text-white'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            {seatNo}
                          </div>
                        );
                      })}
                    </div>

                    {/* 범례 */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-sub-text">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded bg-tayo" />
                        <span>{nameQuery ? '검색 결과' : '선점됨'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded bg-gray-100" />
                        <span>{nameQuery ? '검색 결과 외 좌석' : '비어있음'}</span>
                      </div>
                    </div>
                  </div>

                  {/* 배정 상세 리스트 */}
                  {allocCount > 0 && (
                    <div className="border-t border-border px-5 py-3">
                      <div className="flex flex-wrap gap-2">
                        {group.schedules
                          .filter((s) => s.allocated_seat)
                          .sort((a, b) => a.allocated_seat - b.allocated_seat)
                          .map((s) => (
                            <span key={s.id} className="inline-flex items-center gap-1 text-xs bg-gray-50 rounded-lg px-2.5 py-1.5 border border-gray-100">
                              <span className="font-bold text-tayo">{s.allocated_seat}번</span>
                              <span className="text-charcoal">{s.name || s.student_id || '-'}</span>
                              <span className="text-sub-text">({s.seat_preferences?.join(',') || '-'})</span>
                            </span>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}

          <div className="text-sm text-sub-text text-center">
            총 {busGroups.length}개 버스 · {filtered.length}개 스케줄
          </div>
        </div>
      )}

      {/* ============ 스케줄 목록 뷰 ============ */}
      {!isLoading && viewMode === 'list' && (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sub-text">{nameQuery ? '검색 결과가 없습니다' : '데이터가 없습니다'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-snow">
                  <tr>
                    {['사용자', '요일', '방향', '노선', '시간', '정류장', '선호좌석', '배정'].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-border">
                  {filtered.map((schedule) => (
                    <tr key={schedule.id} className="hover:bg-snow transition-colors">
                      <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                        <div className="font-medium">{schedule.name || '-'}</div>
                        {schedule.student_id && (
                          <div className="text-xs text-sub-text">{schedule.student_id}</div>
                        )}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                        {DAY_KO[schedule.day_of_week] || schedule.day_of_week || '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <span className={`inline-flex text-xs font-semibold rounded-full px-2.5 py-0.5 ${
                          schedule.direction === 'UP'
                            ? 'bg-tayo-light text-tayo'
                            : 'bg-orange-50 text-orange-600'
                        }`}>
                          {schedule.direction === 'UP' ? '등교' : '하교'}
                        </span>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                        {schedule.route_name || '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                        {schedule.bus_time || '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                        {schedule.stop_name || '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-sm text-sub-text">
                        {schedule.seat_preferences?.length
                          ? schedule.seat_preferences.join(', ')
                          : '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {schedule.allocated_seat ? (
                          <span className="inline-flex text-xs font-bold text-tayo bg-tayo-light rounded-full px-2.5 py-0.5">
                            {schedule.allocated_seat}번
                          </span>
                        ) : (
                          <span className="text-xs text-sub-text">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 border-t border-border">
                <p className="text-sm text-sub-text">
                  총 <span className="font-medium text-charcoal">{filtered.length}</span>개 스케줄
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
